import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { formatTimestamp } from '../utils/time';
import { Search, ArrowRight, X } from 'lucide-react';
import type { AgentEvent } from '../utils/eventSchema';
import './MessageLog.css';

const TYPE_COLORS: Record<string, string> = {
  message: 'var(--accent-blue)',
  tool_call: 'var(--accent-purple)',
  error: 'var(--accent-red)',
  system: 'var(--text-dim)',
};

const PAGE_SIZE = 50;

function filterEvents(
  events: AgentEvent[],
  filterType: Set<string>,
  filterAgents: Set<string>,
  searchText: string
): AgentEvent[] {
  return events.filter((e) => {
    if (filterType.size > 0 && !filterType.has(e.event_type)) return false;
    if (filterAgents.size > 0) {
      const agentMatch =
        filterAgents.has(e.agent_id) ||
        (e.source_agent && filterAgents.has(e.source_agent)) ||
        (e.target_agent && filterAgents.has(e.target_agent));
      if (!agentMatch) return false;
    }
    if (searchText) {
      const text = [
        e.content_preview,
        e.content_full,
        e.agent_id,
        e.source_agent,
        e.target_agent,
        ...e.tool_calls,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!text.includes(searchText.toLowerCase())) return false;
    }
    return true;
  });
}

export function MessageLog() {
  const { events, selectedEventIndex, selectEvent } = useSessionStore();
  const [filterType, setFilterType] = useState<Set<string>>(new Set());
  const [filterAgents, setFilterAgents] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [showFilters] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get all agent IDs for filter dropdown
  const allAgents = useMemo(() => {
    const ids = new Set<string>();
    events.forEach((e) => {
      ids.add(e.agent_id);
      if (e.source_agent) ids.add(e.source_agent);
      if (e.target_agent) ids.add(e.target_agent);
    });
    return Array.from(ids).sort();
  }, [events]);

  // Filtered events
  const filteredEvents = useMemo(
    () => filterEvents(events, filterType, filterAgents, search),
    [events, filterType, filterAgents, search]
  );

  // Paginate
  const [page, setPage] = useState(1);
  const visibleEvents = filteredEvents.slice(0, page * PAGE_SIZE);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      if (page * PAGE_SIZE < filteredEvents.length) {
        setPage((p) => p + 1);
      }
    }
  }, [page, filteredEvents.length]);

  function toggleType(t: string) {
    setFilterType((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  }

  function toggleAgent(id: string) {
    setFilterAgents((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function clearFilters() {
    setFilterType(new Set());
    setFilterAgents(new Set());
    setSearch('');
  }

  const hasFilters = filterType.size > 0 || filterAgents.size > 0 || search;

  return (
    <div className="message-log">
      {showFilters && (
        <div className="message-log-filters">
          <div className="filter-search">
            <Search size={12} className="filter-search-icon" />
            <input
              type="text"
              className="filter-search-input"
              placeholder="Search events…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <span className="filter-label">Type</span>
              {(['message', 'tool_call', 'error', 'system'] as const).map((t) => (
                <button
                  key={t}
                  className={`filter-chip ${filterType.has(t) ? 'filter-chip--active' : ''}`}
                  style={{ '--chip-color': TYPE_COLORS[t] } as React.CSSProperties}
                  onClick={() => toggleType(t)}
                >
                  {t.replace('_', ' ')}
                </button>
              ))}
            </div>

            <div className="filter-group">
              <span className="filter-label">Agent</span>
              {allAgents.slice(0, 6).map((id) => (
                <button
                  key={id}
                  className={`filter-chip ${filterAgents.has(id) ? 'filter-chip--active' : ''}`}
                  onClick={() => toggleAgent(id)}
                >
                  {id}
                </button>
              ))}
            </div>

            {hasFilters && (
              <button className="filter-clear-btn" onClick={clearFilters}>
                <X size={10} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className="message-log-list" ref={containerRef} onScroll={handleScroll}>
        {filteredEvents.length === 0 ? (
          <div className="message-log-empty">No events recorded</div>
        ) : (
          visibleEvents.map((event, index) => {
            const isSelected = index === selectedEventIndex;
            const globalIndex = events.indexOf(event);

            return (
              <div
                key={event.event_id}
                className={`message-entry ${isSelected ? 'message-entry--selected' : ''} ${event.event_type === 'error' ? 'message-entry--error' : ''}`}
                onClick={() => selectEvent(isSelected ? null : globalIndex)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && selectEvent(isSelected ? null : globalIndex)}
              >
                <span className="message-index">{globalIndex + 1}</span>
                <span className="message-time">{formatTimestamp(event.timestamp_ms)}</span>
                <span
                  className="message-type-badge"
                  style={{ background: TYPE_COLORS[event.event_type] + '22', color: TYPE_COLORS[event.event_type] }}
                >
                  {event.event_type}
                </span>
                {event.source_agent && (
                  <>
                    <span className="message-agent">{event.source_agent}</span>
                    <ArrowRight size={9} className="message-arrow" />
                  </>
                )}
                <span className="message-agent">{event.target_agent || event.agent_id}</span>
                {event.content_preview && (
                  <span className="message-preview">{event.content_preview.slice(0, 80)}</span>
                )}
                {event.event_type === 'error' && event.error && (
                  <span className="message-error-text">{event.error.slice(0, 60)}</span>
                )}
              </div>
            );
          })
        )}
        {page * PAGE_SIZE < filteredEvents.length && (
          <div className="message-log-load-more" onClick={() => setPage((p) => p + 1)}>
            Load more ({filteredEvents.length - page * PAGE_SIZE} remaining)
          </div>
        )}
      </div>
    </div>
  );
}
